import type { FC, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { memo, useEffect, useRef, useState } from 'react';
import type { SxProps, Theme } from '@mui/material';
import { InputAdornment } from '@mui/material';
import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import { TextField, type Props as TextFieldProps } from 'components/mui/common/TextField';
import type { FormItemUiDefinition } from 'types/formItem';

const iconSx = {
  color: 'action.active',
  opacity: 0.7,
  flexShrink: 0,
  cursor: 'pointer',
  fontSize: '1rem',
} as const;

type SearchableBrowseItemProps = {
  name: string;
  onSearchSubmit: (searchTerm: string, shouldAutoSelect: boolean) => void;
  isSingleBrowse: boolean;
  disabled?: boolean;
  label?: string;
  error?: boolean;
  lightError?: boolean;
  helperText?: ReactNode;
  sx?: SxProps<Theme>;
  slotProps?: TextFieldProps['slotProps'];
  endAdornment?: ReactNode;
  formItemUiDefinition?: FormItemUiDefinition | null;
  onClear?: () => void;
  onBlur?: () => void;
};

const SearchableBrowseItem: FC<SearchableBrowseItemProps> = memo(
  ({
    name,
    onSearchSubmit,
    isSingleBrowse,
    disabled,
    label,
    error,
    lightError,
    helperText,
    sx,
    slotProps,
    endAdornment,
    formItemUiDefinition,
    onClear,
    onBlur,
  }) => {
    const initialValueRef = useRef(name);
    const skipNextBlurRef = useRef(false);
    const [value, setValue] = useState(name);

    const commitSearch = () => {
      const current = value.trim();
      const initial = initialValueRef.current.trim();

      onSearchSubmit(current, current !== initial);

      if (isSingleBrowse) {
        initialValueRef.current = current;
      } else {
        initialValueRef.current = '';
        setValue('');
      }
    };

    const handleClear = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      skipNextBlurRef.current = true;
      setValue('');
      initialValueRef.current = '';
      onClear?.();
    };

    const handleBlur = () => {
      if (skipNextBlurRef.current) {
        skipNextBlurRef.current = false;
        return;
      }

      const current = value.trim();
      const initial = initialValueRef.current.trim();

      if (current !== initial) {
        commitSearch();
        return;
      }

      onBlur?.();
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        skipNextBlurRef.current = true;
        commitSearch();
        e.currentTarget.blur();
      }
    };

    useEffect(() => {
      setValue(name);
      initialValueRef.current = name;
    }, [name]);

    return (
      <TextField
        label={label}
        error={error}
        lightError={lightError}
        helperText={lightError ? undefined : helperText}
        sx={sx}
        slotProps={{
          ...slotProps,
          input: {
            ...slotProps?.input,
            endAdornment: !disabled ? (
              <>
                {value.trim() ? (
                  <InputAdornment onMouseDown={handleClear} position="end">
                    <CloseIcon sx={{ ...iconSx, mr: 0.5 }} />
                  </InputAdornment>
                ) : null}
                <InputAdornment onClick={commitSearch} position="end">
                  <SearchIcon sx={{ ...iconSx, mr: endAdornment ? 0.5 : 0 }} />
                </InputAdornment>
                {endAdornment}
              </>
            ) : undefined,
          },
          htmlInput: {
            ...slotProps?.htmlInput,
            onKeyDown: handleKeyDown,
            enterKeyHint: 'search',
          },
        }}
        formItemUiDefinition={formItemUiDefinition}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleBlur}
        fullWidth
        disabled={disabled}
      />
    );
  }
);

export { SearchableBrowseItem };
